import { ref, computed, watch, inject, Ref, ComputedRef } from 'vue';
import { store } from '@/store';
import { UserState } from '@/composables/User';
import { TypeCampaignEnum } from '@/types';
import { activate as activateTemplate } from '@/composables/template-editor/TemplateEditor';
import {
  EditingTemplate,
  EditingTemplateInformations,
  EditingTemplateStatusEnum,
  EditingTemplateTypeEnum,
} from '@/store/modules/flow-editor';
import { asInt, showToastSuccess, showToastError } from '@/helpers';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { TemplateTypeEnum } from '@/composables/shop/Templates';
import { useTemplateConfig, TemplateConfigOptions } from './useTemplateConfig';
import { i18n } from '@/i18n';
import type { TemplatesCustom } from '@/types/generated-types/graphql';
import { WhatsappValidationStatus } from './useWhatsappTemplateValidation';
import {
  between, maxLength, minLength, integer, helpers,
} from '@vuelidate/validators';
import { parseOperatorParameters } from '@/helpers/flowParameters';
import { getOperatorById } from '@/components/flows/operators/registry';

// ===== Sending Channel Constants =====

export const sendingChannelBoxes = [
  'boxsendemail',
  'boxsendsms',
  'boxsendrcs',
  'boxsendwhatsapp',
  'boxdisplayfreepopup',
  'boxdisplayfreemodel',
  'boxsendfbmessenger',
  'boxsendpushnotification',
];

export const sendingChannelBoxesEnabledDefaults: Record<string, Record<string, number>> = {
  [TypeCampaignEnum.BULK]: {
    boxsendemail: 1,
    boxsendsms: 1,
    boxsendrcs: 1,
    boxsendwhatsapp: 1,
    boxsendsmartmessage: 1,
    boxdisplayfreepopup: 1,
    boxdisplayfreemodel: 1,
    boxsendfbmessenger: 1,
    boxsendpushnotification: 1,
  },
  [TypeCampaignEnum.AUTOMATION]: {
    boxsendemail: 1,
    boxsendsms: 1,
    boxsendrcs: 1,
    boxsendwhatsapp: 0,
    boxsendsmartmessage: 1,
    boxdisplayfreepopup: 1,
    boxdisplayfreemodel: 1,
    boxsendfbmessenger: 1,
    boxsendpushnotification: 1,
  },
};

/**
 * Get the default enabled_operator value for a sending channel box based on campaign type.
 * Falls back to the hardcoded default if no campaign type or box mapping is found.
 */
export const getDefaultEnabledOperator = (boxId: string, campaignType?: string, hardcodedDefault = 1): number => {
  if (campaignType && sendingChannelBoxesEnabledDefaults[campaignType]?.[boxId] !== undefined) {
    return sendingChannelBoxesEnabledDefaults[campaignType][boxId];
  }
  return hardcodedDefault;
};

export enum reusePreviousCouponValues {
  CONFIGURE_NEW_VOUCHER = 0,
  REUSE_FROM_PREVIOUS_BOX = 1,
  USE_EXPIRING_VOUCHER = 2,
}

export enum voucherMethodsValues {
  MANUAL = 2,
  AUTOMATIC = 3,
}

export enum voucherTypeValues {
  PERCENTAGE = 1,
  AMOUNT = 2,
  SHIPPING_FEES = 3,
  DUPLICATED = 4,
}

// ===== Template & Voucher Helpers =====

export const getTemplateAndRedirect = async (idShop: number, idTemplate: number): Promise<TemplatesCustom[] | null> => {
  const response = await nestCall(
    'get',
    NestApiType.CLIENT_API,
    `/templates/${idTemplate}/chain`,
    {},
    '',
    undefined,
    true,
  );

  if (!response || !response.success) {
    throw new Error(response?.message || 'Failed to fetch template chain');
  }

  return response.data as TemplatesCustom[] | null;
};

export const checkIfTemplateOrRedirectContainsVoucher = (templates: TemplatesCustom[]): boolean => {
  let containsVoucher = false;

  if (templates !== null && templates.length) {
    templates.forEach((template) => {
      if (typeof template.template_config === 'string') {
        const templateConfig = JSON.parse(template.template_config);
        if (templateConfig.hasVoucher) {
          containsVoucher = true;
          return false;
        }
      }

      return true;
    });
  }

  return containsVoucher;
};

export interface ParentOperatorData {
  id: string;
  template_display_selector_id?: number;
  reuse_a_previous_coupon?: number;
  template_vouchers?: TemplateVouchersData;
  enabled_operator?: number;
  inclusion?: Record<string, any>;
  exclusion?: Record<string, any>;
  segmentation_config?: any[];
}

export interface SendingChannelConfig {
  listTemplate: TemplatesCustom[] | null;
  templatesContainsVoucher: boolean;
  parentsFilterHaveExpiredVoucherSegment: boolean;
  parentsOperatorAlreadyConfiguredVoucher: boolean;
}

/**
 * Get configuration for a sending channel box (Email, SMS, Whatsapp, Popup, etc.)
 * This version is adapted for the flows system and takes parent operators as parameter
 */
export const getConfigSendingChannelBox = async (
  idShop: number,
  idTemplate: number,
  typeBox: string,
  parentOperators: ParentOperatorData[] = [],
): Promise<SendingChannelConfig> => {
  const listTemplate = await getTemplateAndRedirect(idShop, idTemplate);
  const configSendingChannelBox: SendingChannelConfig = {
    listTemplate,
    templatesContainsVoucher: false,
    parentsFilterHaveExpiredVoucherSegment: false,
    parentsOperatorAlreadyConfiguredVoucher: false,
  };

  if (listTemplate !== null) {
    configSendingChannelBox.templatesContainsVoucher = checkIfTemplateOrRedirectContainsVoucher(listTemplate);
  }

  // If template contains vouchers, check parent operators
  if (configSendingChannelBox.templatesContainsVoucher && parentOperators.length > 0) {
    for (const operatorData of parentOperators) {
      // Check if parent is a customer filter with expiring voucher segment
      if (operatorData.id === 'boxfiltre_3') {
        const segmentInclusion = Object.keys(operatorData.inclusion || {});
        const segmentExclusion = Object.keys(operatorData.exclusion || {});

        if (segmentInclusion.includes('filterhasvouchernearexpiration') || segmentExclusion.includes('filterhasvouchernearexpiration')) {
          configSendingChannelBox.parentsFilterHaveExpiredVoucherSegment = true;
        }
      } else if (operatorData.id === 'boxcontactseg') {
        // Check ContactSegmentation filter for expiring voucher segment
        // segmentation_config structure: [ [{ id: "vouchers", sub_properties: [{ id: "date_to", ... }] }], ... ]
        const segConfig = operatorData.segmentation_config;
        if (Array.isArray(segConfig)) {
          const hasExpiringVoucher = segConfig.some((output: any) =>
            Array.isArray(output) && output.some((group: any) =>
              group?.id === 'vouchers'
              && Array.isArray(group.sub_properties)
              && group.sub_properties.some((prop: any) => prop?.id === 'date_to'),
            ),
          );
          if (hasExpiringVoucher) {
            configSendingChannelBox.parentsFilterHaveExpiredVoucherSegment = true;
          }
        }
      } else if (sendingChannelBoxes.includes(operatorData.id)) {
        // Skip disabled operators (enabled_operator === 0); undefined means enabled (old boxes)
        if (operatorData.enabled_operator === 0) continue;

        // Check if parent sending channel box has configured a voucher
        if (operatorData.template_display_selector_id) {
          const listOfThisTemplate = await getTemplateAndRedirect(idShop, operatorData.template_display_selector_id);
          const thisTemplateHasVoucher = listOfThisTemplate !== null
            ? checkIfTemplateOrRedirectContainsVoucher(listOfThisTemplate) : false;

          if (thisTemplateHasVoucher) {
            // New format: check if any voucher in template_vouchers is actually configured
            if (operatorData.template_vouchers && typeof operatorData.template_vouchers === 'object'
              && Object.keys(operatorData.template_vouchers).length > 0) {
              const hasConfigured = Object.values(operatorData.template_vouchers).some(
                (v) => !v.reuse || v.reuse === reusePreviousCouponValues.CONFIGURE_NEW_VOUCHER,
              );
              if (hasConfigured) {
                configSendingChannelBox.parentsOperatorAlreadyConfiguredVoucher = true;
              }
            } else if (asInt(operatorData.reuse_a_previous_coupon ?? 0) === 0) {
              // Legacy format: root-level reuse_a_previous_coupon
              configSendingChannelBox.parentsOperatorAlreadyConfiguredVoucher = true;
            }
          }
        }
      }

      // Early exit if both conditions are met
      if (configSendingChannelBox.parentsFilterHaveExpiredVoucherSegment
        && configSendingChannelBox.parentsOperatorAlreadyConfiguredVoucher) {
        break;
      }
    }
  }

  return configSendingChannelBox;
};

export interface SendTestSuggestion {
  type: string;
  value: string;
  country?: string;
}

export interface VoucherFields {
  template_display_reduction_method: number;
  template_display_reduction_descript: Record<string, string>;
  template_display_reduction_type: number;
  template_display_reduction_identifiant: string;
  template_display_reduction_amount: number;
  template_display_reduction_duplicate: string;
  template_display_reduction_minorderamount: number;
  template_display_reduction_validtime: number;
  template_display_reduction_prefix: string;
}

export interface MultiVoucherConfigItem {
  method: number;
  descript: Record<string, string>;
  type: number;
  identifiant: string;
  amount: number;
  duplicate: string;
  minorderamount: number;
  validtime: number;
  prefix: string;
  reuse?: number; // 0 = configure new, 1 = reuse from previous box, 2 = use expiring voucher
  disabled?: boolean; // true = voucher config disabled (conditional voucher templates)
}

// Structure: { templateId: MultiVoucherConfigItem } - one voucher config per template
export interface TemplateVouchersData {
  [templateId: string]: MultiVoucherConfigItem;
}

export const createDefaultMultiVoucherConfig = (reuse = reusePreviousCouponValues.CONFIGURE_NEW_VOUCHER): MultiVoucherConfigItem => ({
  method: 0, // No method selected - forces validation error
  descript: {},
  type: 0, // No type selected - forces validation error
  identifiant: '',
  amount: 0,
  duplicate: '',
  minorderamount: 0,
  validtime: 7, // 1 week
  prefix: '',
  reuse,
});

// ===== Shared Voucher Validation (Vuelidate Rules) =====

// Build Vuelidate rules for a single voucher config
// This can be used in operators and MultiVoucherConfig.vue
export const buildVoucherRules = (config: MultiVoucherConfigItem): Record<string, any> => {
  const rules: Record<string, any> = {};

  // Method is always required (use == to handle string/number)
  rules.method = {
    required: helpers.withMessage(
      'automatedScenarios.actions.sending_channel.multi_vouchers_config.validation_error',
      (value: number | string) => !!(value && value != 0),
    ),
  };

  // Type is always required (use == to handle string/number)
  rules.type = {
    required: helpers.withMessage(
      'automatedScenarios.actions.sending_channel.multi_vouchers_config.validation_error',
      (value: number | string) => !!(value && value != 0),
    ),
  };

  // If manual method, identifiant is required (use == to handle string/number)
  if (config.method == voucherMethodsValues.MANUAL) {
    rules.identifiant = {
      required: helpers.withMessage(
        'automatedScenarios.actions.sending_channel.vouchers_config.manual_code.required',
        (value: string) => !!value && value.length >= 2,
      ),
      minLength: minLength(2),
      maxLength: maxLength(45),
    };
  }

  // If automatic method with duplicated type, duplicate is required (use == to handle string/number)
  if (config.method == voucherMethodsValues.AUTOMATIC && config.type == voucherTypeValues.DUPLICATED) {
    rules.duplicate = {
      required: helpers.withMessage(
        'automatedScenarios.actions.sending_channel.vouchers_config.duplicated_code.required',
        (value: string) => !!value && value.length >= 2,
      ),
      minLength: minLength(2),
      maxLength: maxLength(45),
    };
  }

  // Amount validation for percentage and amount types (use == to handle string/number)
  if (config.method && config.method != 0 && [voucherTypeValues.PERCENTAGE, voucherTypeValues.AMOUNT].includes(Number(config.type))) {
    rules.amount = {
      integer,
    };
  }

  // Min order amount for automatic method (use == to handle string/number)
  if (config.method == voucherMethodsValues.AUTOMATIC) {
    rules.minorderamount = {
      between: between(0, 10000000),
    };

    rules.validtime = {
      required: helpers.withMessage(
        'automatedScenarios.actions.sending_channel.vouchers_config.validtime.required',
        (value: number | string) => !!(value && Number(value) >= 1 && Number(value) <= 182),
      ),
      between: between(1, 182),
    };

    rules.prefix = {
      minLength: minLength(2),
      maxLength: maxLength(32),
    };
  }

  return rules;
};

// Build Vuelidate rules for template_vouchers (multiple voucher configs)
// requiredTemplateIds: list of template IDs that must have voucher config
export const buildTemplateVouchersRules = (
  templateVouchers: TemplateVouchersData | undefined,
  requiredTemplateIds: string[] = [],
): Record<string, any> => {
  const templateVouchersRules: Record<string, any> = {};
  const safeVouchers = (templateVouchers && typeof templateVouchers === 'object') ? templateVouchers : undefined;

  // Build rules for all required templates
  for (const templateId of requiredTemplateIds) {
    const config = safeVouchers?.[templateId];

    // Skip validation for templates that are reusing a previous coupon
    if (config?.reuse && config.reuse !== reusePreviousCouponValues.CONFIGURE_NEW_VOUCHER) {
      continue;
    }

    // Skip validation for disabled templates (conditional voucher with toggle off)
    if (config?.disabled === true) {
      continue;
    }

    if (!config) {
      // Template config is missing - create rules that will fail validation
      templateVouchersRules[templateId] = {
        method: {
          required: helpers.withMessage(
            i18n.global.t('automatedScenarios.actions.sending_channel.vouchers_config.configurationRequired'),
            () => false,
          ),
        },
      };
    } else {
      templateVouchersRules[templateId] = buildVoucherRules(config);
    }
  }

  return { template_vouchers: templateVouchersRules };
};

// Migrate old voucher config to new template_vouchers structure if needed
// Also cleans up obsolete configs for templates that no longer have vouchers
// Returns true if any changes were made
export const migrateOldVoucherConfig = (
  data: any,
  templatesWithVoucher: string[],
): boolean => {
  let hasChanges = false;

  // Ensure template_vouchers is an object before operating on it
  if (data.template_vouchers && typeof data.template_vouchers !== 'object') {
    try {
      data.template_vouchers = JSON.parse(data.template_vouchers);
    } catch {
      data.template_vouchers = {};
    }
    hasChanges = true;
  }

  // Clean up obsolete configs for templates that no longer have vouchers
  if (data.template_vouchers && typeof data.template_vouchers === 'object') {
    const existingTemplateIds = Object.keys(data.template_vouchers);
    for (const templateId of existingTemplateIds) {
      if (!templatesWithVoucher.includes(templateId)) {
        delete data.template_vouchers[templateId];
        hasChanges = true;
      }
    }
  }

  // If no templates with voucher, clear all voucher-related fields
  if (templatesWithVoucher.length === 0) {
    // Clear template_vouchers
    if (data.template_vouchers && Object.keys(data.template_vouchers).length > 0) {
      data.template_vouchers = {};
      hasChanges = true;
    }
    // Clear old voucher system fields
    const oldVoucherFields = [
      'template_display_reduction_method',
      'template_display_reduction_descript',
      'template_display_reduction_type',
      'template_display_reduction_identifiant',
      'template_display_reduction_amount',
      'template_display_reduction_duplicate',
      'template_display_reduction_minorderamount',
      'template_display_reduction_validtime',
      'template_display_reduction_prefix',
    ];
    for (const field of oldVoucherFields) {
      if (data[field] !== undefined && data[field] !== null && data[field] !== '' && data[field] !== 0) {
        data[field] = field === 'template_display_reduction_descript' ? {} : 0;
        hasChanges = true;
      }
    }
    return hasChanges;
  }

  // Skip migration if template_vouchers already has valid configs
  if (data.template_vouchers && Object.keys(data.template_vouchers).length > 0) {
    return hasChanges;
  }

  // Check if old config exists and can be migrated. Pre-migration scenarios
  // always had a single voucher block applied to the box, regardless of how
  // many templates the box actually rendered (email + popin pair, multi-lang
  // variants, etc.). When migrating, replicate the same config to every
  // template that expects a voucher so the new validator is satisfied without
  // user re-configuration.
  const canMigrate = templatesWithVoucher.length >= 1
    && data.template_display_reduction_method
    && data.template_display_reduction_method !== 0
    && data.template_display_reduction_type
    && data.template_display_reduction_type !== 0;

  if (!canMigrate) {
    return hasChanges;
  }

  const legacyConfig = {
    method: data.template_display_reduction_method,
    descript: data.template_display_reduction_descript || {},
    type: data.template_display_reduction_type,
    identifiant: data.template_display_reduction_identifiant || '',
    amount: data.template_display_reduction_amount || 0,
    duplicate: data.template_display_reduction_duplicate || '',
    minorderamount: data.template_display_reduction_minorderamount || 0,
    validtime: data.template_display_reduction_validtime || 7,
    prefix: data.template_display_reduction_prefix || '',
  };

  data.template_vouchers = templatesWithVoucher.reduce<Record<string, typeof legacyConfig>>(
    (acc, templateId) => {
      acc[templateId] = { ...legacyConfig };
      return acc;
    },
    {},
  );

  return true;
};

export interface MessageFields {
  template_message_expname?: Record<string, string>;
  template_message_subject?: Record<string, string>;
  template_message_preview_text?: Record<string, string>;
  template_message_expmail?: Record<string, string>;
  template_message_replytomail?: Record<string, string>;
}

export interface SendingChannelDataFields extends VoucherFields, MessageFields {
  template_display_selector_id: number | null;
  reuse_a_previous_coupon?: number;
  template_vouchers?: TemplateVouchersData;
  /** RCS sending box: Vonage agent sender_id (shared Shopimind or dedicated agent). */
  rcs_sender_id?: string;
}

export interface SendingChannelFormOptions {
  media: string;
  templateType?: string;
  dataFields: SendingChannelDataFields;
  formValidation: Ref<any>;
  panelsKey: Ref<number>;
  operatorId?: string;
  onCancel?: () => void;
}

export interface PanelConfig {
  id: string;
  title: string;
  hide?: boolean;
}

export interface ExpiringVoucherFilterInfo {
  filterStepId: string;
  filterStepType: string;
  timePeriod?: { value: number; unit: string };
  codePrefix?: string;
}

export interface ResolvedParentVoucherConfig {
  sourceStepId: string;
  sourceStepType: string;
  sourceLabel?: string;
  voucherConfigs: TemplateVouchersData;
  legacyVoucherConfig?: MultiVoucherConfigItem;
  isExpiringVoucher?: boolean;
  expiringVoucherInfo?: ExpiringVoucherFilterInfo;
  depth: number;
}

export interface ResolvedReusedVoucher {
  configs: ResolvedParentVoucherConfig[];
  hasMultiplePaths: boolean;
  notFound: boolean;
  isExpiringVoucher?: boolean;
  expiringVoucherInfo?: ExpiringVoucherFilterInfo;
}

/**
 * Composable for managing sending channel forms (SMS, Email, Whatsapp, Popup, etc.)
 * Handles template selection, editing, voucher configuration, and message configuration
 */
/**
 * Get all parent operators data from the flow structure up to the root
 */
function getParentOperatorsFromFlow(
  flowData: { steps: Array<{ id: string; type: string; parents: Array<{ id: string }>; parameters: Record<string, string> }> } | null,
  operatorId: string | undefined,
): ParentOperatorData[] {
  if (!flowData || !operatorId) return [];

  const parents: ParentOperatorData[] = [];
  const stepsMap = new Map(flowData.steps.map((s) => [s.id, s]));
  const visited = new Set<string>();

  const collectParents = (stepId: string) => {
    const step = stepsMap.get(stepId);
    if (!step || visited.has(stepId)) return;
    visited.add(stepId);

    for (const parent of step.parents) {
      const parentStep = stepsMap.get(parent.id);
      if (parentStep) {
        // Parse parameters to get the operator data
        const params = parentStep.parameters || {};
        let templateVouchers: TemplateVouchersData | undefined;
        if (params.template_vouchers) {
          try {
            templateVouchers = typeof params.template_vouchers === 'string'
              ? JSON.parse(params.template_vouchers)
              : params.template_vouchers;
          } catch { /* ignore */ }
        }
        let segmentationConfig: any[] | undefined;
        if (params.segmentation_config) {
          try {
            segmentationConfig = typeof params.segmentation_config === 'string'
              ? JSON.parse(params.segmentation_config)
              : params.segmentation_config;
          } catch { /* ignore */ }
        }
        parents.push({
          id: parentStep.type,
          template_display_selector_id: params.template_display_selector_id
            ? parseInt(params.template_display_selector_id, 10)
            : undefined,
          reuse_a_previous_coupon: params.reuse_a_previous_coupon
            ? parseInt(params.reuse_a_previous_coupon, 10)
            : undefined,
          template_vouchers: templateVouchers,
          enabled_operator: params.enabled_operator !== undefined
            ? parseInt(params.enabled_operator, 10)
            : undefined,
          inclusion: params.inclusion ? JSON.parse(params.inclusion) : undefined,
          exclusion: params.exclusion ? JSON.parse(params.exclusion) : undefined,
          segmentation_config: segmentationConfig,
        });
        // Recursively collect parents
        collectParents(parent.id);
      }
    }
  };

  collectParents(operatorId);
  return parents;
}

/**
 * Get parent operators data from a list of ancestor step IDs (used when the operator is not yet saved in the flow)
 */
function getParentOperatorsFromAncestorIds(
  flowData: { steps: Array<{ id: string; type: string; parents: Array<{ id: string }>; parameters: Record<string, string> }> } | null,
  ancestorStepIds: string[],
): ParentOperatorData[] {
  if (!flowData || !ancestorStepIds.length) return [];

  const parents: ParentOperatorData[] = [];
  const stepsMap = new Map(flowData.steps.map((s) => [s.id, s]));

  for (const stepId of ancestorStepIds) {
    const step = stepsMap.get(stepId);
    if (step) {
      const params = step.parameters || {};
      let templateVouchers: TemplateVouchersData | undefined;
      if (params.template_vouchers) {
        try {
          templateVouchers = typeof params.template_vouchers === 'string'
            ? JSON.parse(params.template_vouchers)
            : params.template_vouchers;
        } catch { /* ignore */ }
      }
      let segmentationConfig: any[] | undefined;
      if (params.segmentation_config) {
        try {
          segmentationConfig = typeof params.segmentation_config === 'string'
            ? JSON.parse(params.segmentation_config)
            : params.segmentation_config;
        } catch { /* ignore */ }
      }
      parents.push({
        id: step.type,
        template_display_selector_id: params.template_display_selector_id
          ? parseInt(params.template_display_selector_id, 10)
          : undefined,
        reuse_a_previous_coupon: params.reuse_a_previous_coupon
          ? parseInt(params.reuse_a_previous_coupon, 10)
          : undefined,
        template_vouchers: templateVouchers,
        enabled_operator: params.enabled_operator !== undefined
          ? parseInt(params.enabled_operator, 10)
          : undefined,
        inclusion: params.inclusion ? JSON.parse(params.inclusion) : undefined,
        exclusion: params.exclusion ? JSON.parse(params.exclusion) : undefined,
        segmentation_config: segmentationConfig,
      });
    }
  }

  return parents;
}

type FlowStepData = { id: string; type: string; parents: Array<{ id: string }>; parameters: Record<string, string> };

/**
 * Resolve the reused voucher configuration by traversing parent steps.
 * Follows the chain of "reuse" steps until finding one that actually configures a voucher.
 * Handles multiple paths (branch merges) by returning all resolved configs.
 */
export function resolveReusedVoucherConfigs(
  flowData: { steps: FlowStepData[] } | null,
  operatorId: string | undefined,
  ancestorStepIds?: string[],
): ResolvedReusedVoucher {
  const empty: ResolvedReusedVoucher = { configs: [], hasMultiplePaths: false, notFound: true };
  if (!flowData) return empty;

  const stepsMap = new Map(flowData.steps.map((s) => [s.id, s]));

  // Find the direct parent step IDs of the current operator
  let directParentIds: string[] = [];
  if (operatorId) {
    const currentStep = stepsMap.get(operatorId);
    if (currentStep) {
      directParentIds = currentStep.parents.map((p) => p.id);
    }
  } else if (ancestorStepIds?.length) {
    // Create mode: ancestorStepIds already includes all ancestors, but we need direct parents.
    // The first entries are the direct parents (they are added first in FlowRenderer).
    // However, we can't distinguish direct parents from other ancestors easily.
    // Use all ancestors as starting points and let the traversal handle it.
    directParentIds = [...ancestorStepIds];
  }

  if (!directParentIds.length) return empty;

  const results: ResolvedParentVoucherConfig[] = [];
  const visited = new Set<string>();

  const traverse = (stepId: string, depth: number) => {
    if (visited.has(stepId)) return;
    visited.add(stepId);

    const step = stepsMap.get(stepId);
    if (!step) return;

    const isSendingChannel = sendingChannelBoxes.includes(step.type);

    if (!isSendingChannel) {
      // Not a sending channel box — continue upward through its parents
      for (const parent of step.parents) {
        traverse(parent.id, depth + 1);
      }
      return;
    }

    // It's a sending channel box — parse its parameters
    const params = parseOperatorParameters(step.parameters || {});

    // Skip disabled operators (enabled_operator === 0); undefined means enabled (old boxes)
    const enabledOperator = params.enabled_operator !== undefined ? Number(params.enabled_operator) : undefined;
    if (enabledOperator === 0) {
      for (const parent of step.parents) {
        traverse(parent.id, depth + 1);
      }
      return;
    }
    const reuseValue = typeof params.reuse_a_previous_coupon === 'number'
      ? params.reuse_a_previous_coupon
      : parseInt(params.reuse_a_previous_coupon || '0', 10);

    // Check new format: template_vouchers
    const templateVouchers = params.template_vouchers as TemplateVouchersData | undefined;

    if (templateVouchers && typeof templateVouchers === 'object' && Object.keys(templateVouchers).length > 0) {
      // New format: check if any voucher in this step is actually configured (not reusing)
      const hasConfigured = Object.values(templateVouchers).some(
        (v) => !v.reuse || v.reuse === reusePreviousCouponValues.CONFIGURE_NEW_VOUCHER,
      );

      if (hasConfigured) {
        // This step has real voucher configs — it's a source
        results.push({
          sourceStepId: step.id,
          sourceStepType: step.type,
          voucherConfigs: templateVouchers,
          depth,
        });
        return;
      }

      // No configured voucher in this step (all are reusing or expiring) — continue upward
      for (const parent of step.parents) {
        traverse(parent.id, depth + 1);
      }
      return;
    }

    // Legacy format: check root-level reduction fields
    if (reuseValue === reusePreviousCouponValues.CONFIGURE_NEW_VOUCHER) {
      const method = typeof params.template_display_reduction_method === 'number'
        ? params.template_display_reduction_method
        : parseInt(params.template_display_reduction_method || '0', 10);

      if (method > 0) {
        // Has legacy voucher config — build a TemplateVouchersData from it
        const legacyConfig: MultiVoucherConfigItem = {
          method,
          descript: params.template_display_reduction_descript || {},
          type: typeof params.template_display_reduction_type === 'number'
            ? params.template_display_reduction_type
            : parseInt(params.template_display_reduction_type || '0', 10),
          identifiant: params.template_display_reduction_identifiant || '',
          amount: typeof params.template_display_reduction_amount === 'number'
            ? params.template_display_reduction_amount
            : parseFloat(params.template_display_reduction_amount || '0'),
          duplicate: params.template_display_reduction_duplicate || '',
          minorderamount: typeof params.template_display_reduction_minorderamount === 'number'
            ? params.template_display_reduction_minorderamount
            : parseFloat(params.template_display_reduction_minorderamount || '0'),
          validtime: typeof params.template_display_reduction_validtime === 'number'
            ? params.template_display_reduction_validtime
            : parseInt(params.template_display_reduction_validtime || '7', 10),
          prefix: params.template_display_reduction_prefix || '',
        };

        results.push({
          sourceStepId: step.id,
          sourceStepType: step.type,
          voucherConfigs: { legacy: legacyConfig },
          legacyVoucherConfig: legacyConfig,
          depth,
        });
        return;
      }
    }

    if (reuseValue === reusePreviousCouponValues.REUSE_FROM_PREVIOUS_BOX) {
      // This step is also reusing — continue upward
      for (const parent of step.parents) {
        traverse(parent.id, depth + 1);
      }
      return;
    }

    if (reuseValue === reusePreviousCouponValues.USE_EXPIRING_VOUCHER) {
      // Expiring voucher step — not a real config, but continue upward to find a configured source
      for (const parent of step.parents) {
        traverse(parent.id, depth + 1);
      }
      return;
    }

    // No valid config — stop here, don't traverse further
  };

  for (const parentId of directParentIds) {
    traverse(parentId, 1);
  }

  return {
    configs: results,
    hasMultiplePaths: results.length > 1,
    notFound: results.length === 0,
  };
}

/**
 * Find the expiring voucher filter (boxfiltre_3 with filterhasvouchernearexpiration segment)
 * by searching ancestors of the given step.
 */
function findExpiringVoucherFilter(
  stepsMap: Map<string, FlowStepData>,
  startStepId: string,
): ExpiringVoucherFilterInfo | null {
  const visited = new Set<string>();

  const search = (stepId: string): ExpiringVoucherFilterInfo | null => {
    const step = stepsMap.get(stepId);
    if (!step || visited.has(stepId)) return null;
    visited.add(stepId);

    if (step.type === 'boxfiltre_3') {
      const params = parseOperatorParameters(step.parameters || {});
      const inclusion = params.inclusion as Record<string, any> | undefined;
      const exclusion = params.exclusion as Record<string, any> | undefined;

      const segment = inclusion?.filterhasvouchernearexpiration
        ?? exclusion?.filterhasvouchernearexpiration;

      if (segment) {
        const info: ExpiringVoucherFilterInfo = {
          filterStepId: step.id,
          filterStepType: step.type,
        };

        // Extract time period from the segment data
        const timePeriods = segment.time_unit_select;
        if (Array.isArray(timePeriods) && timePeriods.length > 0) {
          const first = timePeriods[0];
          if (first && typeof first === 'object') {
            info.timePeriod = { value: first.value ?? 1, unit: first.unit ?? 'DAY' };
          }
        }

        // Extract code prefix
        const codePrefixes = segment.code_start;
        if (Array.isArray(codePrefixes) && codePrefixes.length > 0 && codePrefixes[0]) {
          info.codePrefix = codePrefixes[0];
        }

        return info;
      }
    } else if (step.type === 'boxcontactseg') {
      // ContactSegmentation: check segmentation_config for vouchers/date_to
      const params = parseOperatorParameters(step.parameters || {});
      const segConfig = params.segmentation_config;
      if (Array.isArray(segConfig)) {
        for (const output of segConfig) {
          if (!Array.isArray(output)) continue;
          for (const group of output) {
            if (group?.id !== 'vouchers' || !Array.isArray(group.sub_properties)) continue;
            const dateToProp = group.sub_properties.find((prop: any) => prop?.id === 'date_to');
            if (dateToProp) {
              const info: ExpiringVoucherFilterInfo = {
                filterStepId: step.id,
                filterStepType: step.type,
              };
              // Extract time period from sub_property data
              if (dateToProp.data && typeof dateToProp.data === 'object') {
                info.timePeriod = {
                  value: dateToProp.data.value ?? 1,
                  unit: dateToProp.data.unit ?? 'DAY',
                };
              }
              return info;
            }
          }
        }
      }
    }

    // Continue searching through parents
    for (const parent of step.parents) {
      const result = search(parent.id);
      if (result) return result;
    }
    return null;
  };

  return search(startStepId);
}

/**
 * Resolve expiring voucher filter info for the current operator.
 * Used when the current operator itself selects "Use expiring discount voucher".
 */
export function resolveExpiringVoucherInfo(
  flowData: { steps: FlowStepData[] } | null,
  operatorId: string | undefined,
  ancestorStepIds?: string[],
): ExpiringVoucherFilterInfo | null {
  if (!flowData) return null;

  const stepsMap = new Map(flowData.steps.map((s) => [s.id, s]));

  // Get the starting point for search
  let startStepIds: string[] = [];
  if (operatorId) {
    const currentStep = stepsMap.get(operatorId);
    if (currentStep) {
      startStepIds = currentStep.parents.map((p) => p.id);
    }
  } else if (ancestorStepIds?.length) {
    startStepIds = [...ancestorStepIds];
  }

  for (const stepId of startStepIds) {
    const result = findExpiringVoucherFilter(stepsMap, stepId);
    if (result) return result;
  }
  return null;
}

function buildInformationsFromDataFields(
  dataFields: SendingChannelDataFields,
  media: string,
): EditingTemplateInformations | undefined {
  const info: EditingTemplateInformations = {};

  if (dataFields.template_message_expname && Object.keys(dataFields.template_message_expname).length > 0) {
    info.fromName = { ...dataFields.template_message_expname };
  }

  if (media === 'email') {
    if (dataFields.template_message_subject && Object.keys(dataFields.template_message_subject).length > 0) {
      info.subject = { ...dataFields.template_message_subject };
    }
    if (dataFields.template_message_expmail && Object.keys(dataFields.template_message_expmail).length > 0) {
      info.fromEmail = { ...dataFields.template_message_expmail };
    }
    if (dataFields.template_message_preview_text && Object.keys(dataFields.template_message_preview_text).length > 0) {
      info.previewText = { ...dataFields.template_message_preview_text };
    }
  }

  return Object.keys(info).length > 0 ? info : undefined;
}

export function useSendingChannelForm(options: SendingChannelFormOptions) {
  const { media, dataFields, formValidation, panelsKey, onCancel } = options;

  // Flow context from store
  const activeFlow = computed(() => store.getters['flowEditor/getActiveFlow']);
  const campaignId = computed(() => activeFlow.value?.id ?? 0);
  const campaignType = computed(() => activeFlow.value?.type ?? TypeCampaignEnum.AUTOMATION);
  const shopId = computed(() => UserState.activeShop?.id ?? 0);

  // Insert context from flow editor (available via provide/inject in FlowOperatorPanel)
  const insertContext = inject<ComputedRef<{ allAncestorStepIds?: string[] }> | null>('insertContext', null);

  // Callback to persist step data without user clicking save (provided by FlowOperatorPanel)
  const updateStep = inject<((payload: { stepId: string; data: Record<string, any> }) => void) | null>('updateStep', null);

  // Get parent operators from flow structure
  // In edit mode: traverse the flow tree from the operator's step
  // In create mode: use allAncestorStepIds from insertContext (operator not yet saved in flow)
  const parentOperators = computed(() => {
    const flowData = activeFlow.value?.flowData ?? null;

    // Edit mode: operatorId exists, find parents by traversing the flow tree
    if (options.operatorId) {
      return getParentOperatorsFromFlow(flowData, options.operatorId);
    }

    // Create mode: use ancestor step IDs from insert context
    const ancestorStepIds = insertContext?.value?.allAncestorStepIds;
    if (ancestorStepIds?.length) {
      return getParentOperatorsFromAncestorIds(flowData, ancestorStepIds);
    }

    return [];
  });

  // Resolve the reused voucher configuration from parent steps
  const resolvedParentVoucher = computed((): ResolvedReusedVoucher => {
    const flowData = activeFlow.value?.flowData ?? null;
    const ancestorStepIds = insertContext?.value?.allAncestorStepIds;
    return resolveReusedVoucherConfigs(flowData, options.operatorId, ancestorStepIds);
  });

  // Resolve expiring voucher filter info (for when the current box selects "Use expiring voucher")
  const resolvedExpiringVoucher = computed((): ResolvedReusedVoucher => {
    const flowData = activeFlow.value?.flowData ?? null;
    const ancestorStepIds = insertContext?.value?.allAncestorStepIds;
    const filterInfo = resolveExpiringVoucherInfo(flowData, options.operatorId, ancestorStepIds);
    if (!filterInfo) return { configs: [], hasMultiplePaths: false, notFound: true };
    return {
      configs: [],
      hasMultiplePaths: false,
      notFound: false,
      isExpiringVoucher: true,
      expiringVoucherInfo: filterInfo,
    };
  });

  // Initialize template config composable
  const templateConfig = useTemplateConfig({
    media,
    templateType: options.templateType,
    campaignType: campaignType.value,
    initialTemplateId: dataFields.template_display_selector_id,
    parentOperators: parentOperators.value,
  });

  // Form validation errors (reactive to prop changes)
  const formValidationErrors = ref(formValidation.value);
  watch(formValidation, (newVal) => {
    formValidationErrors.value = newVal;
  });

  // Computed for voucher data (subset of dataFields)
  const voucherData = computed({
    get: () => ({
      template_display_reduction_method: dataFields.template_display_reduction_method,
      template_display_reduction_descript: dataFields.template_display_reduction_descript,
      template_display_reduction_type: dataFields.template_display_reduction_type,
      template_display_reduction_identifiant: dataFields.template_display_reduction_identifiant,
      template_display_reduction_amount: dataFields.template_display_reduction_amount,
      template_display_reduction_duplicate: dataFields.template_display_reduction_duplicate,
      template_display_reduction_minorderamount: dataFields.template_display_reduction_minorderamount,
      template_display_reduction_validtime: dataFields.template_display_reduction_validtime,
      template_display_reduction_prefix: dataFields.template_display_reduction_prefix,
    }),
    set: (value: Partial<VoucherFields>) => {
      Object.assign(dataFields, value);
    },
  });

  // Computed for message data (subset of dataFields) - varies by media type
  const messageData = computed({
    get: () => {
      const data: MessageFields = {};
      if (dataFields.template_message_expname !== undefined) {
        data.template_message_expname = dataFields.template_message_expname;
      }
      if (dataFields.template_message_subject !== undefined) {
        data.template_message_subject = dataFields.template_message_subject;
      }
      if (dataFields.template_message_preview_text !== undefined) {
        data.template_message_preview_text = dataFields.template_message_preview_text;
      }
      if (dataFields.template_message_expmail !== undefined) {
        data.template_message_expmail = dataFields.template_message_expmail;
      }
      if (dataFields.template_message_replytomail !== undefined) {
        data.template_message_replytomail = dataFields.template_message_replytomail;
      }
      return data;
    },
    set: (value: Partial<MessageFields>) => {
      Object.assign(dataFields, value);
    },
  });

  // Template selector state (from composable)
  const templateSelectorIsOpen = computed(() => templateConfig.templateSelectorIsOpen.value);

  // Check if voucher panel should be shown (visible when template contains voucher)
  const showVoucherPanel = computed(() => templateConfig.showVoucherConfig.value);

  // Check if voucher config form should be shown (hidden when reusing a previous coupon)
  const showVoucherConfig = computed(() => {
    return templateConfig.showVoucherConfig.value
      && (dataFields.reuse_a_previous_coupon === reusePreviousCouponValues.CONFIGURE_NEW_VOUCHER
        || !templateConfig.showReusePreviousCouponField.value);
  });

  // Check if reuse previous coupon field should be shown
  const showReusePreviousCouponField = computed(() => templateConfig.showReusePreviousCouponField.value);

  // Dynamic options for reuse previous coupon dropdown
  const reusePreviousCouponOptions = computed(() => {
    const options = [
      { label: i18n.global.t('automatedScenarios.actions.sending_channel.template_config.reuse_a_previous_coupon.options.option_1'), value: reusePreviousCouponValues.CONFIGURE_NEW_VOUCHER },
    ];
    if (templateConfig.voucherConfig.parentsOperatorAlreadyConfiguredVoucher.value) {
      options.push({ label: i18n.global.t('automatedScenarios.actions.sending_channel.template_config.reuse_a_previous_coupon.options.option_2'), value: reusePreviousCouponValues.REUSE_FROM_PREVIOUS_BOX });
    }
    if (templateConfig.voucherConfig.parentsFilterHaveExpiredVoucherSegment.value) {
      options.push({ label: i18n.global.t('automatedScenarios.actions.sending_channel.template_config.reuse_a_previous_coupon.options.option_3'), value: reusePreviousCouponValues.USE_EXPIRING_VOUCHER });
    }
    return options;
  });

  // Always use template_vouchers for voucher configuration (both new and migrated old operators)
  const useMultiVoucherConfig = computed(() => {
    return templateConfig.voucherConfig.templatesContainsVoucher.value;
  });

  // Build template_vouchers structure for all templates that contain vouchers
  // Handles: new operators, old operators with root-level reduction data (auto-migration)
  const buildTemplateVouchers = () => {
    const templates = templateConfig.listTemplates.value;
    if (!templates.length) return;
    if (!templateConfig.voucherConfig.templatesContainsVoucher.value) return;

    // Find templates with hasVoucher = true in their config
    const templatesWithVoucher: string[] = [];
    templates.forEach((template) => {
      const config = JSON.parse(template.template_config ?? '{}');
      if (config.hasVoucher === true) {
        templatesWithVoucher.push(String(template.id_template));
      }
    });

    // Legacy fallback: if no template has hasVoucher flag but API says templates contain voucher,
    // treat the primary template as having a voucher (old template format)
    if (templatesWithVoucher.length === 0) {
      templatesWithVoucher.push(String(templates[0].id_template));
    }

    const newTemplateVouchers: TemplateVouchersData = (dataFields.template_vouchers && typeof dataFields.template_vouchers === 'object') ? dataFields.template_vouchers : {};

    // Clean up stale disabled flags: if a template is no longer conditional, remove disabled
    templates.forEach((template) => {
      const config = JSON.parse(template.template_config ?? '{}');
      const tId = String(template.id_template);
      if (newTemplateVouchers[tId]?.disabled && !config.hasConditionalVoucher) {
        newTemplateVouchers[tId].disabled = false;
      }
    });
    let hasChanges = false;

    // Clean up obsolete configs for templates that no longer have vouchers
    const existingTemplateIds = Object.keys(newTemplateVouchers);
    for (const templateId of existingTemplateIds) {
      if (!templatesWithVoucher.includes(templateId)) {
        delete newTemplateVouchers[templateId];
        hasChanges = true;
      }
    }

    // Check if we have old root-level reduction data to migrate (existing operator with old format)
    const hasOldConfig = !!options.operatorId
      && Object.keys(newTemplateVouchers).length === 0
      && (dataFields as any).template_display_reduction_method !== undefined
      && (dataFields as any).template_display_reduction_method > 0;

    const oldReuse = hasOldConfig
      ? ((dataFields as any).reuse_a_previous_coupon ?? reusePreviousCouponValues.CONFIGURE_NEW_VOUCHER)
      : reusePreviousCouponValues.CONFIGURE_NEW_VOUCHER;

    const hasParentVoucher = templateConfig.voucherConfig.parentsOperatorAlreadyConfiguredVoucher.value;

    // Create config for each template with voucher
    templatesWithVoucher.forEach((templateIdStr, index) => {
      if (!newTemplateVouchers[templateIdStr]) {
        if (hasOldConfig && index === 0) {
          // Migration: first template inherits old root-level reduction data
          newTemplateVouchers[templateIdStr] = {
            method: (dataFields as any).template_display_reduction_method ?? 3,
            descript: (dataFields as any).template_display_reduction_descript ?? {},
            type: (dataFields as any).template_display_reduction_type ?? 1,
            identifiant: (dataFields as any).template_display_reduction_identifiant ?? '',
            amount: (dataFields as any).template_display_reduction_amount ?? 0,
            duplicate: (dataFields as any).template_display_reduction_duplicate ?? '',
            minorderamount: (dataFields as any).template_display_reduction_minorderamount ?? 0,
            validtime: (dataFields as any).template_display_reduction_validtime ?? 7,
            prefix: (dataFields as any).template_display_reduction_prefix ?? '',
            reuse: oldReuse,
          };
        } else if (hasOldConfig && index > 0) {
          // Migration: subsequent templates default to reuse (first one already configures a voucher)
          newTemplateVouchers[templateIdStr] = createDefaultMultiVoucherConfig(reusePreviousCouponValues.REUSE_FROM_PREVIOUS_BOX);
        } else {
          // New config: first depends on parent, subsequent always reuse
          const hasExpiringVoucher = templateConfig.voucherConfig.parentsFilterHaveExpiredVoucherSegment.value;
          let reuse: reusePreviousCouponValues;
          if (index > 0 || hasParentVoucher) {
            reuse = reusePreviousCouponValues.REUSE_FROM_PREVIOUS_BOX;
          } else if (hasExpiringVoucher) {
            reuse = reusePreviousCouponValues.USE_EXPIRING_VOUCHER;
          } else {
            reuse = reusePreviousCouponValues.CONFIGURE_NEW_VOUCHER;
          }
          newTemplateVouchers[templateIdStr] = createDefaultMultiVoucherConfig(reuse);
        }
        hasChanges = true;
      }
    });

    // Fix stale reuse values: if a parent box was disabled, "reuse from previous" or
    // "use expiring voucher" may no longer be available — fall back to a valid option
    const hasExpiringOption = templateConfig.voucherConfig.parentsFilterHaveExpiredVoucherSegment.value;
    let hasStaleReuseFix = false;
    for (const templateId of Object.keys(newTemplateVouchers)) {
      const voucher = newTemplateVouchers[templateId];
      // Don't reset intra-box reuse: non-first templates can always reuse from a preceding template
      const templateIndex = templatesWithVoucher.indexOf(templateId);
      const hasPrecedingInSameBox = templateIndex > 0;
      if (voucher.reuse === reusePreviousCouponValues.REUSE_FROM_PREVIOUS_BOX && !hasParentVoucher && !hasPrecedingInSameBox) {
        voucher.reuse = hasExpiringOption
          ? reusePreviousCouponValues.USE_EXPIRING_VOUCHER
          : reusePreviousCouponValues.CONFIGURE_NEW_VOUCHER;
        hasChanges = true;
        hasStaleReuseFix = true;
      } else if (voucher.reuse === reusePreviousCouponValues.USE_EXPIRING_VOUCHER && !hasExpiringOption) {
        voucher.reuse = hasParentVoucher
          ? reusePreviousCouponValues.REUSE_FROM_PREVIOUS_BOX
          : reusePreviousCouponValues.CONFIGURE_NEW_VOUCHER;
        hasChanges = true;
        hasStaleReuseFix = true;
      }
    }

    if (hasChanges) {
      dataFields.template_vouchers = newTemplateVouchers;
    }

    // Persist stale reuse fix directly on the step so it survives cancel
    if (hasStaleReuseFix && options.operatorId && updateStep) {
      updateStep({
        stepId: options.operatorId,
        data: { template_vouchers: newTemplateVouchers },
      });
    }
  };

  // Watch for template list changes to build template_vouchers
  watch(
    () => templateConfig.listTemplates.value,
    () => {
      buildTemplateVouchers();
    },
    { immediate: true, deep: true },
  );

  // WhatsApp revalidation modal state
  const showWhatsAppRevalidationModal = ref(false);
  const pendingEditTemplate = ref<{ idTemplate: number; typeTemplate: string; labelTemplate: string } | null>(null);

  // Check if current template is WhatsApp and approved
  const isWhatsAppApproved = computed(() => {
    return media === 'whatsapp'
      && (dataFields as any).template_validation_status === WhatsappValidationStatus.APPROVED;
  });

  // Helper to open template editor with root template support
  const openTemplateEditor = async (idTemplate: number, labelTemplate: string, typeTemplate: string) => {
    // Check if template has a root template (for linked templates)
    const selectedTemplate = templateConfig.listTemplates.value.find((item) => item.id_template === idTemplate);
    const selectedTemplateConfig = JSON.parse(selectedTemplate?.template_config ?? '{}');

    if (selectedTemplateConfig.root_template_id) {
      const rootTemplate = templateConfig.listTemplates.value.find(
        (item) => item.id_template === selectedTemplateConfig.root_template_id,
      );
      if (rootTemplate) {
        await activateTemplate(
          idTemplate,
          labelTemplate,
          typeTemplate,
          -1,
          true,
          { idTemplate: rootTemplate.id_template, type: rootTemplate.type || '', name: rootTemplate.label },
        );
      } else {
        store.commit('flowEditor/setEditingTemplate', null);
        throw new Error('Root template not found');
      }
    } else {
      await activateTemplate(idTemplate, labelTemplate, typeTemplate);
    }
  };

  // Handle template selection
  const handleSelectTemplate = async (idTemplate: number, label: string) => {
    store.commit('general/showTheSpinner');

    // Update data fields and load template config
    dataFields.template_display_selector_id = idTemplate;
    await templateConfig.loadTemplateConfig(idTemplate, parentOperators.value);

    // Get template type BEFORE closing selector (in case it resets)
    const typeTemplate = templateConfig.typeTemplateSelector.value;

    // Set editing template state in store (include current form informations for two-way sync)
    const dataEditingTemplate: EditingTemplate = {
      status: EditingTemplateStatusEnum.IN_TEMPLATE_EDITOR,
      id_campaign: campaignId.value,
      idTemplate,
      type: EditingTemplateTypeEnum.EDIT,
      informations: buildInformationsFromDataFields(dataFields, media),
    };
    store.commit('flowEditor/setEditingTemplate', dataEditingTemplate);

    // Open template editor using .then()/.catch() pattern (same as ActionConfig.vue)
    openTemplateEditor(idTemplate, label, typeTemplate).then(() => {
      store.commit('general/hideTheSpinner');
      // Close template selector AFTER editor is opened
      templateConfig.closeTemplateSelector();
      panelsKey.value += 1;
    }).catch((error) => {
      console.error('Error opening template editor:', error);
      store.commit('flowEditor/setEditingTemplate', null);
      store.commit('general/hideTheSpinner');
      // Close template selector even on error
      templateConfig.closeTemplateSelector();
      panelsKey.value += 1;
    });
  };

  // Handle close template selector
  const handleCloseTemplateSelector = () => {
    // If no template was selected, cancel the operator addition
    if (!dataFields.template_display_selector_id && onCancel) {
      onCancel();
      return;
    }
    templateConfig.closeTemplateSelector();
  };

  // Handle change template
  const handleChangeTemplate = (idTemplate: number, typeTemplate: string) => {
    templateConfig.openTemplateSelector(typeTemplate);
  };

  // Handle edit template - with WhatsApp revalidation check
  const handleEditTemplate = async (idTemplate: number, typeTemplate: string, labelTemplate: string) => {
    // Check if this is an approved WhatsApp template - show confirmation modal
    if (isWhatsAppApproved.value && typeTemplate === 'whatsapp') {
      pendingEditTemplate.value = { idTemplate, typeTemplate, labelTemplate };
      showWhatsAppRevalidationModal.value = true;
      return;
    }

    // Proceed with normal edit
    await proceedWithEditTemplate(idTemplate, typeTemplate, labelTemplate);
  };

  // Actually open the template editor (called after confirmation or directly)
  const proceedWithEditTemplate = async (idTemplate: number, typeTemplate: string, labelTemplate: string) => {
    store.commit('general/showTheSpinner');

    // Set editing template state in store (include current form informations for two-way sync)
    const dataEditingTemplate: EditingTemplate = {
      status: EditingTemplateStatusEnum.IN_TEMPLATE_EDITOR,
      id_campaign: campaignId.value,
      idTemplate,
      type: EditingTemplateTypeEnum.EDIT,
      informations: buildInformationsFromDataFields(dataFields, media),
    };
    store.commit('flowEditor/setEditingTemplate', dataEditingTemplate);

    // Open template editor with root template support
    await openTemplateEditor(idTemplate, labelTemplate, typeTemplate).then(() => {
      store.commit('general/hideTheSpinner');
    }).catch(() => {
      store.commit('flowEditor/setEditingTemplate', null);
      store.commit('general/hideTheSpinner');
    });
  };

  // Confirm WhatsApp revalidation - user accepted to edit approved template
  const confirmWhatsAppRevalidation = async () => {
    if (pendingEditTemplate.value) {
      const { idTemplate, typeTemplate, labelTemplate } = pendingEditTemplate.value;
      showWhatsAppRevalidationModal.value = false;
      pendingEditTemplate.value = null;
      await proceedWithEditTemplate(idTemplate, typeTemplate, labelTemplate);
    }
  };

  // Cancel WhatsApp revalidation - user declined to edit
  const cancelWhatsAppRevalidation = () => {
    showWhatsAppRevalidationModal.value = false;
    pendingEditTemplate.value = null;
  };

  // Send test modal state
  const showSendTestModal = ref(false);
  const isValidatingSendTest = ref(false);

  // Handle opening send test modal (with optional validation callback)
  const handleOpenSendTestModal = async (validateFn?: () => Promise<{ success: boolean; validate?: any }>) => {
    // If validation function provided, validate first
    if (validateFn) {
      isValidatingSendTest.value = true;
      try {
        const validation = await validateFn();
        if (!validation.success) {
          // Show validation errors
          formValidationErrors.value = validation.validate || {};
          panelsKey.value += 1;
          await showToastError(i18n.global.t('errorMessages.FORM_ERROR'));
          
          // Scroll to first error input after DOM update
          setTimeout(() => {
            const firstErrorElement = document.querySelector('.has-error');
            if (firstErrorElement) {
              firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
          return;
        }
      } catch (error) {
        console.error('Validation error:', error);
        return;
      } finally {
        isValidatingSendTest.value = false;
      }
    }

    // Check if active template is V4
    const activeTemplate = templateConfig.activePreviewTemplate.value;
    const activeTemplateData = templateConfig.listTemplates.value.find(
      (t) => t.id_template === activeTemplate.id_template,
    );
    const isV4 = activeTemplateData?.version === 4;

    const shouldUseUnifiedModal = (media === 'email' && isV4) || media === 'sms' || media === 'whatsapp' || media === 'richtexting';
    if (shouldUseUnifiedModal) {
      store.commit('general/showSendTestEmailV4Modal', activeTemplate.id_template);
    } else {
      showSendTestModal.value = true;
      store.commit('general/showSendTestModal');
    }
  };

  // Handle closing send test modal
  const handleCloseSendTestModal = () => {
    showSendTestModal.value = false;
  };

  // Build and execute the send test API call
  const executeSendTest = async (
    params: SendTestSuggestion[] | SendTestSuggestion,
    contactId?: number | null,
    langs?: string[],
    conditionOverrides?: Record<string, boolean>,
    rcsSenderIdOverride?: string,
  ) => {
    const activeTemplate = templateConfig.activePreviewTemplate.value;
    if (!activeTemplate.id_template || !activeTemplate.type) return;

    store.commit('general/showTheSpinner');

    try {
      // Build request data
      const getData: Record<string, any> = {
        type: activeTemplate.type,
        params,
      };

      if (contactId) {
        getData.contactId = contactId;
      }
      if (Array.isArray(langs) && langs.length > 0) {
        getData.langs = langs;
      }
      if (conditionOverrides && Object.keys(conditionOverrides).length > 0) {
        getData.conditionOverrides = conditionOverrides;
      }

      // Add message informations for email/sms/whatsapp
      if ([TemplateTypeEnum.EMAIL, TemplateTypeEnum.SMS, TemplateTypeEnum.RICHTEXTING, TemplateTypeEnum.WHATSAPP].includes(activeTemplate.type as TemplateTypeEnum)) {
        const resolvedRcsSenderId = rcsSenderIdOverride || dataFields.rcs_sender_id;
        getData.informations = activeTemplate.type === TemplateTypeEnum.RICHTEXTING
          ? { rcsSenderId: resolvedRcsSenderId }
          : { fromName: dataFields.template_message_expname };

        if (activeTemplate.type === TemplateTypeEnum.RICHTEXTING && resolvedRcsSenderId) {
          getData.senderId = resolvedRcsSenderId;
          getData.rcsSenderId = resolvedRcsSenderId;
        }

        if (activeTemplate.type === TemplateTypeEnum.EMAIL) {
          getData.informations.fromEmail = dataFields.template_message_expmail;
          getData.informations.subject = dataFields.template_message_subject;
          getData.informations.previewText = dataFields.template_message_preview_text;
        }
      }

      const dataQuery = btoa(encodeURIComponent(JSON.stringify(getData)));
      const result = await nestCall('get', NestApiType.DEFAULT, `/template/test/${shopId.value}/${activeTemplate.id_template}/${dataQuery}`, {}, '', {}, false);

      if (result && result.success) {
        if (activeTemplate.type === TemplateTypeEnum.EMAIL) {
          await showToastSuccess('emailSent');
        } else if (activeTemplate.type === TemplateTypeEnum.SMS) {
          await showToastSuccess('smsSent');
        } else if (activeTemplate.type === TemplateTypeEnum.WHATSAPP) {
          await showToastSuccess('whatsappSent');
        } else {
          await showToastSuccess('testSent');
        }
      } else {
        await showToastError(result && result.message ? i18n.global.t(`errorMessages.${result.message}`) : i18n.global.t('errorMessages.GENERIC_ERROR'));
      }
    } catch (error) {
      console.error('Send test error:', error);
      await showToastError(i18n.global.t('errorMessages.GENERIC_ERROR'));
    } finally {
      store.commit('general/hideTheSpinner');
      showSendTestModal.value = false;
    }
  };

  // Handle send test from legacy modal (SMS, WhatsApp, Push, etc.)
  const handleSendTest = async (params: SendTestSuggestion[]) => {
    await executeSendTest(params);
  };

  // Handle send test from V4 modal (email with contact selection)
  const handleSendTestV4 = async (
    payload: {
      params: SendTestSuggestion[] | SendTestSuggestion;
      channel?: 'email' | 'sms' | 'whatsapp' | 'richtexting';
      contactId: number | null;
      langs?: string[];
      conditionOverrides?: Record<string, boolean>;
      senderId?: string;
      rcsSenderId?: string;
    },
  ) => {
    const rcsSenderId = payload.rcsSenderId || payload.senderId;
    if (rcsSenderId && dataFields.rcs_sender_id !== undefined) {
      dataFields.rcs_sender_id = rcsSenderId;
    }
    await executeSendTest(
      payload.params,
      payload.contactId,
      payload.langs,
      payload.conditionOverrides,
      rcsSenderId,
    );
  };

  // Watch for template editor save/quit events from flowEditor store
  const editingTemplate = computed(() => store.getters['flowEditor/getEditingTemplate']);
  watch(editingTemplate, async (newValue) => {
    // Reset images templates preview
    templateConfig.resetImagePreview();

    if (newValue !== null) {
      if ([EditingTemplateStatusEnum.SAVE_QUIT_TEMPLATE_EDITOR, EditingTemplateStatusEnum.SAVE_STAY_TEMPLATE_EDITOR].includes(newValue.status)) {
        // Reload template config after save to detect voucher changes
        if (dataFields.template_display_selector_id) {
          await templateConfig.loadTemplateConfig(asInt(dataFields.template_display_selector_id), parentOperators.value);
        }

        // Sync informations from template editor back to form dataFields
        if (newValue.informations) {
          const info = newValue.informations;
          if (info.subject && Object.keys(info.subject).length > 0 && dataFields.template_message_subject !== undefined) {
            dataFields.template_message_subject = { ...info.subject };
          }
          if (info.previewText && Object.keys(info.previewText).length > 0 && dataFields.template_message_preview_text !== undefined) {
            dataFields.template_message_preview_text = { ...info.previewText };
          }
          if (info.fromEmail && Object.keys(info.fromEmail).length > 0 && dataFields.template_message_expmail !== undefined) {
            dataFields.template_message_expmail = { ...info.fromEmail };
            if (dataFields.template_message_replytomail !== undefined) {
              dataFields.template_message_replytomail = { ...info.fromEmail };
            }
          }
          if (info.fromName && Object.keys(info.fromName).length > 0 && dataFields.template_message_expname !== undefined) {
            dataFields.template_message_expname = { ...info.fromName };
          }
        }

        panelsKey.value += 1;

        if (newValue.status === EditingTemplateStatusEnum.SAVE_QUIT_TEMPLATE_EDITOR) {
          store.commit('flowEditor/setEditingTemplate', null);
        } else if (newValue.status === EditingTemplateStatusEnum.SAVE_STAY_TEMPLATE_EDITOR) {
          store.commit('flowEditor/setEditingTemplate', {
            ...newValue,
            status: EditingTemplateStatusEnum.IN_TEMPLATE_EDITOR,
          });
        }
      } else if (newValue.status === EditingTemplateStatusEnum.QUIT_TEMPLATE_EDITOR) {
        store.commit('flowEditor/setEditingTemplate', null);
      }
    }
  }, { deep: true });

  // Initialize the form (call this in onMounted)
  const initialize = async (templateId: number | null) => {
    await templateConfig.initialize(templateId, parentOperators.value);
  };

  return {
    // Flow context
    campaignId,
    campaignType,
    shopId,

    // Template config (expose the full composable for advanced usage)
    templateConfig,
    templateSelectorIsOpen,

    // Form validation
    formValidationErrors,

    // Data computed
    voucherData,
    messageData,
    showVoucherPanel,
    showVoucherConfig,
    showReusePreviousCouponField,
    reusePreviousCouponOptions,
    useMultiVoucherConfig,
    resolvedParentVoucher,
    resolvedExpiringVoucher,

    // Handlers
    handleSelectTemplate,
    handleCloseTemplateSelector,
    handleChangeTemplate,
    handleEditTemplate,
    handleSendTest,
    handleSendTestV4,
    handleOpenSendTestModal,
    handleCloseSendTestModal,

    // Send test modal state
    showSendTestModal,
    isValidatingSendTest,

    // WhatsApp revalidation modal
    showWhatsAppRevalidationModal,
    confirmWhatsAppRevalidation,
    cancelWhatsAppRevalidation,

    // Initialization
    initialize,
  };
}
